import { useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useStore } from '@tanstack/react-form';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { PageHeader } from '@/common/components/page-header/PageHeader';
import { Button } from '@/common/components/ui/button';
import { SelectItem } from '@/common/components/ui/select';
import { useSupplyPoint } from '@/features/supply-points/context/useSupplyPoint';
import { deviceQueries, deviceQueryKeys } from '@/features/devices/queries';
import { DeviceService } from '@/features/devices/services/DeviceService';
import { PARENT_NONE, makeDeviceFormSchema } from '@/features/devices/config';
import { ParameterField } from '@/features/devices/components/parameter-field/ParameterField';
import { useDeviceParameters } from '@/features/devices/hooks/useDeviceParameters';

interface EditDevicePageProps {
  deviceId: number;
}

export function EditDevicePage({ deviceId }: EditDevicePageProps) {
  const { activeSupplyPoint } = useSupplyPoint();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: devices } = useSuspenseQuery(
    deviceQueries.listByEnergySystem(activeSupplyPoint!.id)
  );
  const { data: deviceTypes } = useSuspenseQuery(deviceQueries.types());
  const { data: existingDevice } = useSuspenseQuery(deviceQueries.detail(deviceId));
  const { data: existingParameters } = useSuspenseQuery(deviceQueries.parameters(deviceId));

  const parameterDefinitions = useMemo(
    () =>
      deviceTypes
        .find((t) => t.id === existingDevice.deviceType)
        ?.parameterdefinitionSet.filter((d) => d.role === 'parameter') ?? [],
    [deviceTypes, existingDevice.deviceType]
  );

  const paramInitialValues = useMemo(() => {
    const vals: Record<number, string> = {};
    for (const def of parameterDefinitions) vals[def.id] = def.value ?? '';
    for (const p of existingParameters) vals[p.parameterDefinition.id] = p.value;
    return vals;
  }, [parameterDefinitions, existingParameters]);

  const params = useDeviceParameters(parameterDefinitions, {
    deviceId,
    existingParameterIds: existingParameters.map((p) => p.parameterDefinition.id),
    initialValues: paramInitialValues,
    onSuccess: (count) => {
      if (count > 0) toast.success(t`Parameters saved`);
    },
  });

  const formSchema = useMemo(() => makeDeviceFormSchema(deviceTypes), [deviceTypes]);

  const form = useAppForm(
    appFormOptions({
      schema: formSchema,
      defaultValues: {
        name: existingDevice?.name ?? null,
        deviceType: existingDevice?.deviceType != null ? String(existingDevice.deviceType) : null,
        parent: existingDevice?.parent != null ? String(existingDevice.parent) : PARENT_NONE,
      },
      onSubmit: async ({ value }) => {
        const selectedType = deviceTypes.find((t) => t.id === value.deviceType);
        const requiresParent = (selectedType?.deviceGroup.parent ?? null) !== null;

        await DeviceService.partialUpdate(existingDevice.id, {
          name: value.name,
          deviceType: value.deviceType,
          parent: requiresParent ? value.parent : null,
        });
        await queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });
        toast.success(t`Device updated`);
      },
    })
  );

  const selectedDeviceTypeId = useStore(form.store, (s) => s.values.deviceType);
  const selectedDeviceType = deviceTypes.find((t) => t.id === Number(selectedDeviceTypeId));
  const parentGroupId = selectedDeviceType?.deviceGroup.parent ?? null;
  const isParentRequired = parentGroupId !== null;

  const parentGroupTypeIds =
    parentGroupId !== null
      ? deviceTypes.filter((t) => t.deviceGroup.id === parentGroupId).map((t) => t.id)
      : [];
  const availableParents =
    parentGroupId !== null
      ? devices.filter(
          (d) => d.id !== existingDevice?.id && parentGroupTypeIds.includes(d.deviceType)
        )
      : [];

  return (
    <div className="flex flex-col gap-6 max-w-3xl pb-10">
      <PageHeader
        title={<Trans>Edit device</Trans>}
        subtitle={<Trans>Configure the device parameters and connection.</Trans>}
      />

      {/* Basic configuration */}
      <form.AppForm>
        <form.FormElement>
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-base font-semibold">
                <Trans>Basic configuration</Trans>
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                <Trans>General information about this device.</Trans>
              </p>
            </div>
            <div className="px-6 py-6 flex flex-col gap-6">
              <FormErrors />
              <div className="grid gap-4 md:grid-cols-2">
                <form.AppField name="name">
                  {(field) => (
                    <field.TextField
                      label={<Trans>Device name</Trans>}
                      placeholder={t`e.g. Solar Inverter A`}
                      required
                    />
                  )}
                </form.AppField>

                <form.AppField name="deviceType">
                  {(field) => (
                    <field.SelectField
                      label={<Trans>Device type</Trans>}
                      required
                      placeholder={t`Select type`}
                    >
                      {deviceTypes.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </field.SelectField>
                  )}
                </form.AppField>

                {isParentRequired && (
                  <div className="flex flex-col gap-1">
                    <form.AppField name="parent">
                      {(field) => (
                        <field.SelectField
                          label={<Trans>Parent device</Trans>}
                          required
                          placeholder={t`Select parent`}
                          disabled={availableParents.length === 0}
                        >
                          {availableParents.map((d) => {
                            const typeName =
                              deviceTypes.find((t) => t.id === d.deviceType)?.name ?? '';
                            return (
                              <SelectItem key={d.id} value={String(d.id)}>
                                {d.name} ({typeName})
                              </SelectItem>
                            );
                          })}
                        </field.SelectField>
                      )}
                    </form.AppField>
                    {availableParents.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        <Trans>
                          No devices available in the required group. Add a parent device first.
                        </Trans>
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <FormSubmit>
                  <Trans>Save</Trans>
                </FormSubmit>
              </div>
            </div>
          </div>
        </form.FormElement>
      </form.AppForm>

      {/* Parameters */}
      {parameterDefinitions.length > 0 && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-base font-semibold">
              <Trans>Parameters</Trans>
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              <Trans>Attributes required for this device type.</Trans>
            </p>
          </div>
          <div className="px-6 py-6 flex flex-col gap-6">
            <div className="grid gap-4 md:grid-cols-2">
              {parameterDefinitions.map((def) => (
                <ParameterField key={def.id} definition={def} {...params.getFieldProps(def)} />
              ))}
            </div>
            <div className="flex justify-end">
              <Button type="button" onClick={() => void params.save()} disabled={params.isSaving}>
                <Trans>Save parameters</Trans>
              </Button>
            </div>
          </div>
        </div>
      )}

      <div>
        <Button variant="outline" type="button" onClick={() => navigate({ to: '/devices' })}>
          <Trans>Back to devices</Trans>
        </Button>
      </div>
    </div>
  );
}
